import i18n, { resources } from './i18n'
import propriedadePt from './vocabulario/propriedade.pt.json'
import propriedadeEn from './vocabulario/propriedade.en.json'
import empresaPropriedadesPt from './vocabulario/empresa-propriedades.pt.json'
import empresaPropriedadesEn from './vocabulario/empresa-propriedades.en.json'
import empresaMistaPt from './vocabulario/empresa-mista.pt.json'
import empresaMistaEn from './vocabulario/empresa-mista.en.json'
import { getContexto } from '../utils/contexto'
import type { ComposicaoEmpresa } from '../hooks/useComposicaoEmpresa'
import type { EstateType } from '../utils/types'

// Uma propriedade usa os mesmos ecrãs do condomínio; só muda o que se lê. Há duas camadas por cima
// dos textos base, cada uma com só as chaves que mudam, por namespace:
//   - a do estate activo: numa propriedade, "propriedade" em vez de "condomínio", "ocupante" em vez
//     de "condómino" (vocabulario/propriedade.*.json);
//   - a da empresa, para os ecrãs da empresa toda (Papéis, Definições, Módulos): conforme a carteira
//     tem só propriedades ou os dois tipos (vocabulario/empresa-*.json).
// As camadas não partilham chaves (o gerador dos JSON verifica-o). Trocar uma delas recompõe os
// namespaces afectados a partir de uma cópia intacta das bases — o i18next altera os objectos que
// recebe, por isso a cópia é tirada aqui, antes de qualquer troca, e nunca se lhe passa o original.
type Bundles = Record<string, Record<string, object>>

const BASE = structuredClone(resources) as unknown as Bundles
const PROPRIEDADE: Bundles = { pt: propriedadePt, en: propriedadeEn }
const EMPRESA: Record<Exclude<ComposicaoEmpresa, 'CONDOMINIUM'>, Bundles> = {
  PROPERTIES: { pt: empresaPropriedadesPt, en: empresaPropriedadesEn },
  MISTO: { pt: empresaMistaPt, en: empresaMistaEn },
}

let estate: EstateType = 'CONDOMINIUM'
let empresa: ComposicaoEmpresa = 'CONDOMINIUM'

/** O vocabulário por omissão do subdomínio: prop.<domínio> fala de propriedades, o resto de condomínios. */
export function tipoDoSubdominio(): EstateType {
  return getContexto() === 'PROPRIEDADE' ? 'PROPERTIES' : 'CONDOMINIUM'
}

/**
 * Passa a interface para o vocabulário de um tipo de estate. Os componentes voltam a desenhar-se
 * sozinhos (o i18n está ligado aos eventos do store — ver `bindI18nStore` em i18n.ts).
 */
export function aplicarVocabulario(tipo: EstateType | null | undefined) {
  const alvo: EstateType = tipo === 'PROPERTIES' ? 'PROPERTIES' : 'CONDOMINIUM'
  if (alvo === estate) return
  estate = alvo
  recompor()
}

/** O vocabulário dos ecrãs da empresa, conforme a carteira (sem saber, o de sempre). */
export function aplicarVocabularioEmpresa(composicao: ComposicaoEmpresa | null) {
  const alvo: ComposicaoEmpresa = composicao ?? 'CONDOMINIUM'
  if (alvo === empresa) return
  empresa = alvo
  recompor()
}

function juntar(destino: Record<string, unknown>, origem: object) {
  for (const [chave, valor] of Object.entries(origem)) {
    const actual = destino[chave]
    if (valor && typeof valor === 'object' && actual && typeof actual === 'object') {
      juntar(actual as Record<string, unknown>, valor)
    } else {
      destino[chave] = valor
    }
  }
}

function recompor() {
  const camadas = [empresa === 'CONDOMINIUM' ? null : EMPRESA[empresa], estate === 'PROPERTIES' ? PROPRIEDADE : null]
  for (const lng of Object.keys(PROPRIEDADE)) {
    // Todos os namespaces que alguma camada mexe: os que deixam de ter camada também voltam à base.
    const namespaces = new Set([PROPRIEDADE, ...Object.values(EMPRESA)].flatMap((c) => Object.keys(c[lng] ?? {})))
    for (const ns of namespaces) {
      const bundle = structuredClone(BASE[lng][ns]) as Record<string, unknown>
      for (const camada of camadas) {
        const chaves = camada?.[lng]?.[ns]
        if (chaves) juntar(bundle, structuredClone(chaves))
      }
      i18n.addResourceBundle(lng, ns, bundle, true, true)
    }
  }
}
